import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { OrderEndpoints } from '../../../services/orderService';

export const fetchOrderHistory = createAsyncThunk(
    'orders/fetchOrderHistory',
    async (_, { rejectWithValue }) => {
        try {
            const response = await OrderEndpoints.userOrderHistory();
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const userOrderHistorySearchAndFilter = createAsyncThunk(
    'orders/userOrderHistorySearchAndFilter',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await OrderEndpoints.userOrderHistorySearchAndFilter(formData);
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchTrackOrderStatus = createAsyncThunk(
    'orders/fetchTrackOrderStatus',
    async (orderid, { rejectWithValue }) => {
        try {
            const response = await OrderEndpoints.fetchTrackOrderStatus(orderid);
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const userMakesOrderAsDelivered = createAsyncThunk(
    'orders/fetchUserMakesOrderAsDelivered',
    async (orderId, { rejectWithValue, dispatch }) => {
        try {
            const response = await OrderEndpoints.userMarkOrderAsReviewed(orderId);
            // Dispatch optimistic update
            dispatch(updateOrderStatusOptimistic({ orderId }));
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const userAddOrUpdateOrderFeedback = createAsyncThunk(
    'orders/userAddOrUpdateOrderFeedback',
    async ({ orderId, formData }, { rejectWithValue, dispatch }) => {
        try {
            const response = await OrderEndpoints.addOrUpdateUserFeedbackForOrder(orderId, formData);
            dispatch(updateOrderFeedbackLocally({ 
                orderId, 
                feedback: formData 
            }));
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const orderSlice = createSlice({
    name: 'userOrder',
    initialState: {
        userOrderHistory: [],
        userOrderHistoryLoading: false,
        userOrderHistoryError: null,

        orderTrackingStatusDetails: null,
        orderTrackingStatusDetailsLoading: false,
        orderTrackingStatusDetailsError: null,

        markAsDeliveredLoading: false,
        markAsDeliveredError: null,
        markAsDelivered: null,

        feedbackLoading: false,
        feedbackError: null,
        feedback: null,
    },
    reducers: {
        updateOrderStatusOptimistic: (state, action) => {
            const { orderId } = action.payload;
         
            const index = state.userOrderHistory.findIndex(
                o => String(o.order_number) === String(orderId)
            );
 
            if (index !== -1) {
                state.userOrderHistory = state.userOrderHistory.map((order, i) =>
                    i === index ? { ...order, status: 'Delivered' } : order
                );
            }
        },
        updateOrderFeedbackLocally: (state, action) => {
            const { orderId, feedback } = action.payload;
            const index = state.userOrderHistory.findIndex(
                o => String(o.order_number) === String(orderId)
            );
            if (index !== -1) {
                state.userOrderHistory = state.userOrderHistory.map((order, i) =>
                    i === index ? { 
                        ...order, 
                        feedback: {
                            ...order.feedback,
                            ...feedback
                        }
                    } : order
                );
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Order History
            .addCase(fetchOrderHistory.pending, (state) => {
                state.userOrderHistoryLoading = true;
                state.userOrderHistoryError = null;
            })
            .addCase(fetchOrderHistory.fulfilled, (state, action) => {
                state.userOrderHistoryLoading = false;
                state.userOrderHistory = action.payload;
            })
            .addCase(fetchOrderHistory.rejected, (state, action) => {
                state.userOrderHistoryLoading = false;
                state.userOrderHistoryError = action.payload;
            })
            
            // Search and Filter Orders
            .addCase(userOrderHistorySearchAndFilter.pending, (state) => {
                state.userOrderHistoryLoading = true;
                state.userOrderHistoryError = null;
            })
            .addCase(userOrderHistorySearchAndFilter.fulfilled, (state, action) => {
                state.userOrderHistoryLoading = false;
                state.userOrderHistory = action.payload;
            })
            .addCase(userOrderHistorySearchAndFilter.rejected, (state, action) => {
                state.userOrderHistoryLoading = false;
                state.userOrderHistoryError = action.payload;
            })

            // Track Order Status
            .addCase(fetchTrackOrderStatus.pending, (state) => {
                state.orderTrackingStatusDetailsLoading = true;
                state.orderTrackingStatusDetailsError = null;
            })
            .addCase(fetchTrackOrderStatus.fulfilled, (state, action) => {
                state.orderTrackingStatusDetailsLoading = false;
                state.orderTrackingStatusDetails = action.payload;
            })
            .addCase(fetchTrackOrderStatus.rejected, (state, action) => {
                state.orderTrackingStatusDetailsLoading = false;
                state.orderTrackingStatusDetailsError = action.payload;
            })
            
            // Mark as Delivered
            .addCase(userMakesOrderAsDelivered.pending, (state) => {
                state.markAsDeliveredLoading = true;
                state.markAsDeliveredError = null;
            })
            .addCase(userMakesOrderAsDelivered.fulfilled, (state, action) => {
                state.markAsDeliveredLoading = false;
                state.markAsDelivered = action.payload;
            })
            .addCase(userMakesOrderAsDelivered.rejected, (state, action) => {
                state.markAsDeliveredLoading = false;
                state.markAsDeliveredError = action.payload;
            })
            
            // Add/Update Feedback
            .addCase(userAddOrUpdateOrderFeedback.pending, (state) => {
                state.feedbackLoading = true;
                state.feedbackError = null;
            })
            .addCase(userAddOrUpdateOrderFeedback.fulfilled, (state, action) => {
                state.feedbackLoading = false;
                state.feedback = action.payload;
            })
            .addCase(userAddOrUpdateOrderFeedback.rejected, (state, action) => {
                state.feedbackLoading = false;
                state.feedbackError = action.payload;
            });
    },
});

export const { updateOrderStatusOptimistic, updateOrderFeedbackLocally } = orderSlice.actions;
export default orderSlice.reducer;